import { Check, Share2, UserMinus, X } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogSrTitle,
} from '@/components/ui/dialog';
import { Avatar } from '@/components/ui/avatar';
import { useKbMutation } from '@/hooks/useKb';
import { kb, users } from '@/lib/api/endpoints';
import type { KbMember, UserPublic } from '@/lib/api/types';
import { keys } from '@/lib/queryClient';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * Sharing one topic.
 *
 * The disclosure line is the heart of it: before the owner confirms, the
 * dialog says exactly what the grant hands over — "N notes, of which M
 * sensitive". A shelf of credentials must never be shared on muscle memory.
 */
export function KbShareDialog({
  topicId,
  open,
  onClose,
}: {
  topicId: string;
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<'VIEWER' | 'EDITOR'>('VIEWER');

  const { data: members } = useQuery({
    queryKey: ['kb', 'members', topicId],
    queryFn: () => kb.members(topicId),
    enabled: open,
  });
  const { data: disclosure } = useQuery({
    queryKey: ['kb', 'disclosure', topicId],
    queryFn: () => kb.shareDisclosure(topicId),
    enabled: open,
  });
  const { data: matches } = useQuery({
    queryKey: keys.userSearch(query.trim()),
    queryFn: ({ signal }) => users.search(query.trim(), signal),
    enabled: open && query.trim().length >= 2,
    staleTime: 30_000,
  });

  const share = useKbMutation(
    ({ userId }: { userId: string }) => kb.share(topicId, userId, role),
    () => setQuery(''),
  );
  const changeRole = useKbMutation(
    ({ userId, next }: { userId: string; next: 'VIEWER' | 'EDITOR' }) =>
      kb.changeRole(topicId, userId, next),
  );
  const revoke = useKbMutation(({ userId }: { userId: string }) =>
    kb.revoke(topicId, userId),
  );

  const memberIds = new Set((members ?? []).map((member) => member.user.id));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogSrTitle>{t('kb.share')}</DialogSrTitle>
        <DialogCloseButton />

        <div className="p-5">
          <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
            <Share2 className="h-4 w-4 text-accent" aria-hidden />
            {t('kb.share')}
          </h2>
          {disclosure ? (
            <p
              className={cn(
                'mb-4 text-xs',
                disclosure.sensitive_count > 0 ? 'text-overdue' : 'text-content-subtle',
              )}
            >
              {t('kb.shareDisclosure', {
                notes: disclosure.note_count,
                sensitive: disclosure.sensitive_count,
              })}
            </p>
          ) : null}

          <div className="mb-2 flex gap-2">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('kb.searchPeople')}
              className={cn(
                'h-9 flex-1 rounded-md border border-border bg-surface px-3 text-sm',
                'outline-none placeholder:text-content-subtle focus:border-accent',
              )}
            />
            <RolePicker value={role} onChange={setRole} />
          </div>

          {(matches ?? [])
            .filter((match) => !memberIds.has(match.id))
            .map((match: UserPublic) => (
              <button
                key={match.id}
                type="button"
                onClick={() => share.mutate({ userId: match.id })}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left',
                  'transition-surface hover:bg-surface-hover',
                )}
              >
                <Avatar user={match} size="sm" />
                <span className="flex-1 truncate text-sm">{match.display_name}</span>
                <span className="text-xs text-content-subtle">@{match.username}</span>
              </button>
            ))}

          {(members ?? []).length > 0 ? (
            <ul className="mt-4 space-y-1 border-t border-border pt-3">
              {(members ?? []).map((member: KbMember) => (
                <li key={member.user.id} className="flex items-center gap-2.5">
                  <Avatar user={member.user} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {member.user.display_name}
                  </span>
                  <RolePicker
                    value={member.role}
                    onChange={(next) =>
                      changeRole.mutate({ userId: member.user.id, next })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => revoke.mutate({ userId: member.user.id })}
                    aria-label={t('kb.revoke', { who: member.user.display_name })}
                    className="rounded p-1 text-content-subtle hover:text-danger"
                  >
                    <UserMinus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RolePicker({
  value,
  onChange,
}: {
  value: 'VIEWER' | 'EDITOR';
  onChange: (role: 'VIEWER' | 'EDITOR') => void;
}) {
  const t = useT();
  return (
    <div className="flex gap-0.5 rounded-md bg-surface-sunken p-0.5">
      {(['VIEWER', 'EDITOR'] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={cn(
            'rounded px-2 py-1 text-[11px] font-medium transition-colors',
            option === value
              ? 'bg-surface text-content shadow-sm'
              : 'text-content-muted hover:text-content',
          )}
        >
          {option === value ? (
            <Check className="mr-0.5 inline h-3 w-3" aria-hidden />
          ) : null}
          {t(option === 'VIEWER' ? 'kb.roleViewer' : 'kb.roleEditor')}
        </button>
      ))}
    </div>
  );
}

/** What a member (not the owner) sees instead: whose topic it is + leave. */
export function KbMembershipBar({
  topic,
  onLeft,
}: {
  topic: { id: string; my_role: string; owner: UserPublic | null };
  onLeft: () => void;
}) {
  const t = useT();
  const leave = useKbMutation(() => kb.leave(topic.id), onLeft);
  if (topic.my_role === 'OWNER' || !topic.owner) return null;

  return (
    <div className="mb-4 flex items-center gap-2 rounded-xl bg-surface-sunken px-3 py-2 text-xs text-content-muted">
      <Share2 className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
      {t('kb.sharedBy', {
        who: topic.owner.display_name,
        role: t(topic.my_role === 'EDITOR' ? 'kb.roleEditor' : 'kb.roleViewer'),
      })}
      <span className="flex-1" />
      <button
        type="button"
        onClick={() => leave.mutate(undefined)}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-surface-hover hover:text-danger"
      >
        <X className="h-3 w-3" aria-hidden />
        {t('kb.leave')}
      </button>
    </div>
  );
}
